/**
 * DocumentCommentsSidebar.js
 *
 * Sidebar panel for viewing, adding, replying to, and resolving
 * inline document comments. Syncs with backend via REST endpoints.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useConfirm } from './ConfirmProvider';

// ---- Icons ----
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
);
const IconReply = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/></svg>
);
const IconDelete = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
);

function CommentThread({ comment, currentUser, onResolve, onReply, onDelete, activeCommentId, onFocusComment }) {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);
  const isActive = activeCommentId === String(comment.id);
  const isAuthor = currentUser?.id === comment.author;

  const handleReply = () => {
    if (!replyText.trim()) return;
    onReply(comment.id, replyText.trim());
    setReplyText('');
    setShowReply(false);
  };

  return (
    <div
      className={`doc-comment-thread${isActive ? ' active' : ''}${comment.is_resolved ? ' resolved' : ''}`}
      onClick={() => onFocusComment(String(comment.id))}
    >
      {/* Top-level comment */}
      <div className="doc-comment-header">
        <span className="doc-comment-author">{comment.author_full_name || 'Unknown'}</span>
        <span className="doc-comment-time">{new Date(comment.created_at).toLocaleString()}</span>
      </div>

      {comment.highlighted_text && (
        <div className="doc-comment-highlight-preview">"{comment.highlighted_text}"</div>
      )}

      <div className="doc-comment-body">{comment.content}</div>

      {/* Replies */}
      {comment.replies?.map((reply) => (
        <div key={reply.id} className="doc-comment-reply">
          <div className="doc-comment-header">
            <span className="doc-comment-author">{reply.author_full_name || 'Unknown'}</span>
            <span className="doc-comment-time">{new Date(reply.created_at).toLocaleString()}</span>
          </div>
          <div className="doc-comment-body">{reply.content}</div>
        </div>
      ))}

      {/* Actions */}
      <div className="doc-comment-actions">
        {!comment.is_resolved && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setShowReply(!showReply); }} title="Reply">
              <IconReply /> Reply
            </button>
            <button onClick={(e) => { e.stopPropagation(); onResolve(comment.id); }} title="Resolve">
              <IconCheck /> Resolve
            </button>
          </>
        )}
        {comment.is_resolved && (
          <span className="doc-comment-resolved-badge">Resolved</span>
        )}
        {isAuthor && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(comment.id); }} title="Delete" className="danger">
            <IconDelete />
          </button>
        )}
      </div>

      {/* Reply input */}
      {showReply && (
        <div className="doc-comment-reply-box">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            rows={2}
          />
          <div className="doc-comment-reply-actions">
            <button onClick={() => { setShowReply(false); setReplyText(''); }}>Cancel</button>
            <button onClick={handleReply} className="primary" disabled={!replyText.trim()}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentCommentsSidebar({
  authAxios,
  documentId,
  editor,
  currentUser,
  showFormMessage,
}) {
  const confirm = useConfirm();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const inputRef = useRef(null);

  // Fetch comments
  const fetchComments = useCallback(async () => {
    if (!authAxios || !documentId) return;
    setLoading(true);
    try {
      const resp = await authAxios.get(`document-comments/?document=${documentId}`);
      setComments(resp.data?.results || resp.data || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
    } finally {
      setLoading(false);
    }
  }, [authAxios, documentId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Add comment on the current selection
  const handleAddComment = useCallback(async () => {
    if (!newComment.trim() || !authAxios) return;

    // Guard: document must be saved first
    if (!documentId) {
      showFormMessage?.('Please save the document before adding comments.', 'error');
      return;
    }

    const { from, to } = editor?.state?.selection || {};
    const selectedText = editor?.state?.doc?.textBetween(from, to, ' ') || '';

    try {
      const resp = await authAxios.post('document-comments/', {
        document: documentId,
        content: newComment.trim(),
        highlighted_text: selectedText,
        position_from: from || 0,
        position_to: to || 0,
      });

      // Apply comment mark to the selected text
      if (editor && selectedText && resp.data?.id) {
        editor.chain().focus().setComment(String(resp.data.id)).run();
      }

      setNewComment('');
      fetchComments();
      showFormMessage?.('Comment added.', 'success');
    } catch (err) {
      console.error('Failed to add comment:', err);
      const detail = err?.response?.data?.detail || err?.response?.data?.error || 'Failed to add comment.';
      showFormMessage?.(detail, 'error');
    }
  }, [newComment, authAxios, documentId, editor, fetchComments, showFormMessage]);

  // Resolve comment
  const handleResolve = useCallback(async (commentId) => {
    try {
      await authAxios.post(`document-comments/${commentId}/resolve/`);
      fetchComments();
    } catch (err) {
      console.error('Failed to resolve comment:', err);
    }
  }, [authAxios, fetchComments]);

  // Reply to comment
  const handleReply = useCallback(async (parentId, content) => {
    try {
      await authAxios.post('document-comments/', {
        document: documentId,
        content,
        parent: parentId,
      });
      fetchComments();
    } catch (err) {
      console.error('Failed to reply:', err);
    }
  }, [authAxios, documentId, fetchComments]);

  // Delete comment
  const handleDelete = useCallback(async (commentId) => {
    if (!(await confirm({ title: 'Delete Comment', message: 'Delete this comment?', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await authAxios.delete(`document-comments/${commentId}/`);
      fetchComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  }, [authAxios, fetchComments, confirm]);

  // Focus a comment highlight in the editor
  const handleFocusComment = useCallback((commentId) => {
    setActiveCommentId(commentId);
    if (!editor) return;

    // Find the comment mark in the document and scroll to it
    const { doc } = editor.state;
    let found = false;
    doc.descendants((node, pos) => {
      if (found) return false;
      const marks = node.marks || [];
      for (const mark of marks) {
        if (mark.type.name === 'comment' && mark.attrs.commentId === commentId) {
          editor.commands.setTextSelection(pos);
          found = true;
          return false;
        }
      }
    });
  }, [editor]);

  const filteredComments = showResolved
    ? comments
    : comments.filter((c) => !c.is_resolved);

  return (
    <div className="doc-comments-sidebar">
      <div className="doc-comments-header">
        <h3>Comments</h3>
        <label className="doc-comments-filter">
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>
      </div>

      {/* New comment input */}
      <div className="doc-comment-new">
        <textarea
          ref={inputRef}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Select text, then add a comment..."
          rows={3}
        />
        <button
          onClick={handleAddComment}
          disabled={!newComment.trim()}
          className="primary"
        >
          Add Comment
        </button>
      </div>

      {/* Comment list */}
      <div className="doc-comments-list">
        {loading && <div className="doc-comments-loading">Loading comments...</div>}
        {!loading && filteredComments.length === 0 && (
          <div className="doc-comments-empty">
            No comments yet. Select text and add a comment.
          </div>
        )}
        {filteredComments.map((comment) => (
          <CommentThread
            key={comment.id}
            comment={comment}
            currentUser={currentUser}
            onResolve={handleResolve}
            onReply={handleReply}
            onDelete={handleDelete}
            activeCommentId={activeCommentId}
            onFocusComment={handleFocusComment}
          />
        ))}
      </div>
    </div>
  );
}
